import React, { useCallback, useMemo, useState, useRef } from 'react';

import {
  ReactFlow,
  addEdge,
  reconnectEdge,
  applyNodeChanges,
  applyEdgeChanges,
  useReactFlow,
  SelectionMode,
  useNodesInitialized,
  useStoreApi,
} from '@xyflow/react';
import { v4 as uuidv4 } from 'uuid';

import '@xyflow/react/dist/style.css';
import './index.css';

import { flowchartLogger } from 'src/lib/logger';

import DialplanMiniMap from './components/DialplanMiniMap';
import { edgeHighlightContext } from './context/edgeHighlightContext';
import { mouseSelectionContext } from './context/mouseSelectionContext';
import edgeTypes from './edges/edgeTypes';
import NodeSelectorNode from './nodes/NodeSelector/NodeSelectorNode';

import type { NodeMenuConfig } from './dataProvider/types';
import type {
  Node,
  Edge,
  OnConnect,
  OnEdgesChange,
  OnNodesChange,
  NodeMouseHandler,
  OnMove,
  Viewport,
  NodeTypes,
  OnConnectStartParams,
  NodeProps,
  CoordinateExtent,
} from '@xyflow/react';

const leftXBoundary = 0;

/**
 * Keeps nodes from being dragged past the left edge of the canvas, leaving the other sides unbounded.
 * React Flow clamps against this while the drag gesture runs, which `onNodesChange` cannot do: the drag
 * handler writes the node position straight into the store before notifying us.
 *
 * It has to be handed to `ReactFlowProvider` as well as `ReactFlow`, because React Flow re-clamps every
 * `nodes` prop update against the extent the provider's store was created with.
 */
export const nodeExtent: CoordinateExtent = [
  [leftXBoundary, Number.NEGATIVE_INFINITY],
  [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY],
];

/**
 * Prepares the node types for the flowchart merging the existing node types with the default node types.
 *
 * @param {NodeTypes} nodeTypes - The existing node types.
 * @param {NodeMenuConfig} nodeMenuConfig - The configuration for the node menu.
 * @returns {NodeTypes} The updated node types with the node-selector node type added.
 */
export const prepareNodeTypes = (nodeTypes: NodeTypes, nodeMenuConfig: NodeMenuConfig): NodeTypes => {
  return {
    ...nodeTypes,
    'node-selector': (props: NodeProps) => <NodeSelectorNode {...props} nodeMenuConfig={nodeMenuConfig} />,
  };
};

interface FlowProps {
  initialNodes?: Node[];
  initialEdges?: Edge[];
  minimapVisible?: boolean;
  recordChanged?: boolean;
  nodeTypes?: NodeTypes | undefined;
  nodeMenuConfig: NodeMenuConfig;
  setRecordChanged?: (value: boolean) => void;
  onNodeClick?: NodeMouseHandler | undefined;
}

const Flow = ({
  initialNodes = [],
  initialEdges = [],
  minimapVisible = true,
  recordChanged = true,
  nodeTypes = undefined,
  nodeMenuConfig,
  setRecordChanged = () => {},
  onNodeClick = undefined,
}: FlowProps): React.ReactElement => {
  const [nodes, setNodes] = useState<Node[]>(initialNodes ?? []);
  const [edges, setEdges] = useState<Edge[]>(initialEdges ?? []);
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);
  const connectingNodeSource = useRef<{ nodeId: string; handleId: string } | null>(null);
  const justCreatedNodeSelector = useRef<boolean>(false);

  const { setViewport, getViewport } = useReactFlow();
  const store = useStoreApi();
  const nodesFocusable = store.getState().nodesFocusable;
  const { useMouseSelection } = React.useContext(mouseSelectionContext);
  const nodesInitialized = useNodesInitialized();

  const cancelNodeSelector = useCallback(() => {
    setNodes((nds) => nds.filter((node) => node.type !== 'node-selector'));
    connectingNodeSource.current = null;
    justCreatedNodeSelector.current = false;
  }, []);

  const preparedNodeTypes = useMemo(
    () => prepareNodeTypes(nodeTypes ?? {}, nodeMenuConfig),
    [nodeTypes, nodeMenuConfig]
  );

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => {
      // Check if any changes should trigger recordChanged before applying them
      const shouldMarkChanged =
        setRecordChanged !== undefined &&
        !recordChanged &&
        nodesInitialized &&
        changes.some((change) => change.type !== 'dimensions');

      setNodes((nds) => {
        const parsedChanges = changes.map((change) => {
          if (change.type !== 'position' || change.position === undefined) return change;

          // Lock the x position of the node if it has a fixedX value. Use this to lock any node to a specific x position.
          const fixedX = nds.find((node) => change.id === node.id)?.data.fixedX;
          if (typeof fixedX !== 'number') return change;

          flowchartLogger.debug('Locking node position to x:', fixedX);
          return { ...change, position: { x: fixedX, y: change.position.y } };
        });

        return applyNodeChanges(parsedChanges, nds);
      });

      // Update parent state after our state update completes
      if (shouldMarkChanged) {
        setRecordChanged(true);
      }
    },
    [setNodes, nodesInitialized, setRecordChanged, recordChanged]
  );
  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      setEdges((eds) => applyEdgeChanges(changes, eds));
    },
    [setEdges]
  );
  const onMove: OnMove = useCallback(
    (event: TouchEvent | MouseEvent | null, viewport: Viewport) => {
      flowchartLogger.debug('onMove', { event, viewport });
      const leftXBoundaryWithPadding = leftXBoundary; // Add any number to leftXBoundary to add padding
      if (viewport.x > leftXBoundaryWithPadding) {
        void setViewport({
          x: leftXBoundaryWithPadding,
          y: viewport.y,
          zoom: viewport.zoom,
        });
      }
    },
    [setViewport]
  );
  const onConnect: OnConnect = useCallback(
    (connection) => {
      // reset the start node on connections
      connectingNodeSource.current = null;
      setEdges((edges) => {
        const edge = edges.find(
          (edge) =>
            edge.source === connection.source &&
            (edge.sourceHandle === connection.sourceHandle || edge.sourceHandle === 'simple')
        );
        setRecordChanged?.(true);
        if (edge !== undefined) {
          // If there is an edge that is connected to the same node source, update the edge
          return reconnectEdge(edge, connection, edges);
        }
        return addEdge(connection, edges);
      });
    },
    [setEdges, setRecordChanged]
  );
  const onConnectStart = useCallback(
    (_event: MouseEvent | TouchEvent, { nodeId, handleId, handleType }: OnConnectStartParams) => {
      if (handleType === 'source' && nodeId !== null && handleId !== null) {
        connectingNodeSource.current = { nodeId, handleId };
      }
    },
    []
  );

  const onEdgeMouseEnter = useCallback((_event: React.MouseEvent, edge: Edge) => {
    setHoveredEdgeId(edge.id);
  }, []);

  const onEdgeMouseLeave = useCallback((_event: React.MouseEvent, edge: Edge) => {
    setHoveredEdgeId((current) => (current === edge.id ? null : current));
  }, []);

  /**
   * onConnectEnd is called when the user releases the mouse button or touch event after starting a connection.
   * We show the node selector node and create a new node at the position of the mouse release.
   */
  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (connectingNodeSource.current === null || !nodesFocusable) return;

      const target = event.target as HTMLElement;
      const targetIsPane = target.classList.contains('react-flow__pane');

      if (targetIsPane && event instanceof MouseEvent) {
        // Get the pane dimensions for coordinate conversion
        const pane = target.getBoundingClientRect();

        // Calculate mouse position relative to the pane
        const x = event.clientX - pane.left;
        const y = event.clientY - pane.top;

        // Get viewport to convert screen coordinates to flow coordinates
        const viewport = getViewport();

        // Convert screen coordinates to flow coordinates
        // Flow coordinates = (screen pixels - viewport pan) / zoom
        const flowX = (x - viewport.x) / viewport.zoom;
        const flowY = (y - viewport.y) / viewport.zoom;

        const newNode = {
          id: uuidv4(),
          position: {
            x: flowX,
            y: flowY - 100, // Offset upward to avoid overlapping with cursor
          },
          type: 'node-selector',
          data: {},
        };

        setNodes((nds) => nds.concat(newNode));
        justCreatedNodeSelector.current = true;
        setEdges((eds) => {
          // Check if there's already an edge from the source node with the same source handle
          const existingEdge = eds.find(
            (edge) =>
              edge.source === connectingNodeSource.current?.nodeId &&
              (edge.sourceHandle === connectingNodeSource.current?.handleId || edge.sourceHandle === 'simple')
          );

          if (existingEdge) {
            // If an edge exists, update its target to the new node (replace the old connection)
            return eds.map((edge) =>
              edge.id === existingEdge.id
                ? {
                    ...edge,
                    target: newNode.id,
                  }
                : edge
            );
          } else {
            // If no edge exists, create a new one
            return eds.concat({
              id: `${connectingNodeSource.current?.nodeId ?? ''}-${newNode.id}`,
              source: connectingNodeSource.current?.nodeId ?? '',
              target: newNode.id,
              sourceHandle: connectingNodeSource.current?.handleId ?? 'simple',
            });
          }
        });
      }
    },
    [getViewport, nodesFocusable]
  );

  const onPaneClick = useCallback(() => {
    // Don't cancel if we just created the node selector
    if (justCreatedNodeSelector.current) {
      justCreatedNodeSelector.current = false;
      return;
    }

    // Cancel node selector if it exists
    const hasNodeSelector = nodes.some((node) => node.type === 'node-selector');
    if (hasNodeSelector) {
      cancelNodeSelector();
    }
  }, [nodes, cancelNodeSelector]);

  return (
    <edgeHighlightContext.Provider value={{ hoveredEdgeId }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onMove={onMove}
        onConnect={onConnect}
        onConnectStart={onConnectStart}
        onConnectEnd={onConnectEnd}
        onEdgeMouseEnter={onEdgeMouseEnter}
        onEdgeMouseLeave={onEdgeMouseLeave}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        fitView
        nodeExtent={nodeExtent}
        defaultViewport={{ zoom: 0.8, x: leftXBoundary, y: 200 }}
        fitViewOptions={{
          padding: 0.2,
          duration: 1000,
          minZoom: 0.5,
        }}
        minZoom={0.2}
        defaultEdgeOptions={{
          animated: false,
          zIndex: 5000,
        }}
        panOnScroll={useMouseSelection}
        selectionOnDrag={useMouseSelection}
        panOnDrag={useMouseSelection ? [1, 2] : true}
        selectionMode={SelectionMode.Partial}
        edgeTypes={edgeTypes}
        nodeTypes={preparedNodeTypes}
        proOptions={{ hideAttribution: true }}
      >
        {nodes.length > 0 && minimapVisible && (
          <>
            <DialplanMiniMap />
          </>
        )}
      </ReactFlow>
    </edgeHighlightContext.Provider>
  );
};

export default Flow;
